import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
    Loader2,
    Plus,
    FileDown,
    FileUp,
    X,
    Receipt,
    Store,
    Calendar,
    Filter,
    RefreshCw
} from "lucide-react";

import { DataTable } from "../components/ui/DataTable";
import SearchableSelectBox from "../components/ui/SearchableSelectBox";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import { fetchPaymentsReceived, fetchStoresWhereStaffAssigned } from "../apiHelper";
import { useToast } from "../hooks/useToast";

const PaymentsReceived = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // User & Store State
    const [user, setUser] = useState(null);
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState("");

    // Date Filter State (Defaults to current date YYYY-MM-DD)
    const [filterDate, setFilterDate] = useState(() => new Date().toISOString().split("T")[0]);
    const [useDateFilter, setUseDateFilter] = useState(true);

    // Payments & UI State
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showActions, setShowActions] = useState(false);

    // 1. Initialize User & Branch Locations
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast({ message: "Please login to view payments.", show: true, toastType: 0 });
            navigate("/login");
            return;
        }
        try {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);

            const loadStores = async () => {
                try {
                    const returnedStores = await fetchStoresWhereStaffAssigned(parsedUser);
                    const fetchedStores = (returnedStores || []).filter((s) => s.storetype !== "WHS");
                    if (fetchedStores && fetchedStores.length > 0) {
                        const mappedStores = fetchedStores.map((store) => ({
                            label: store.identifier || `Branch ${store.store_id}`,
                            value: store.store_id.toString(),
                        }));
                        setStores(mappedStores);
                        setSelectedStore(mappedStores[0].value);
                    } else {
                        showToast({ message: "No branch locations assigned.", show: true, toastType: 2 });
                    }
                } catch (err) {
                    showToast({ message: "Failed to load branch locations.", show: true, toastType: 2 });
                }
            };

            loadStores();
        } catch {
            showToast({ message: "Invalid session.", show: true, toastType: 2 });
            navigate("/login");
        }
    }, [showToast, navigate]);

    // 2. Fetch Payments (Includes store_id and payment_date in Payload)
    const loadPayments = useCallback(async () => {
        if (!user?.users_id || !selectedStore) return;

        setLoading(true);
        try {
            const payload = {
                ...user,
                store_id: Number(selectedStore),
                offset: 0,
                limit: 100,
                ...(useDateFilter && filterDate ? { payment_date: filterDate } : {}),
            };

            // console.log(payload)

            const data = await fetchPaymentsReceived(payload);
            setPayments(Array.isArray(data) ? data : []);
        } catch (err) {
            showToast({ message: "Failed to load payments received.", show: true, toastType: 2 });
            setPayments([]);
        } finally {
            setLoading(false);
        }
    }, [user, selectedStore, filterDate, useDateFilter, showToast]);

    useEffect(() => {
        loadPayments();
    }, [loadPayments]);

    // Helpers
    const formatDateTime = (d) => (d ? format(new Date(d), "d MMM yyyy, hh:mm a") : "—");
    const formatDate = (d) => (d ? format(new Date(d), "d MMM yyyy") : "—");
    const formatCurrency = (a) =>
        a == null
            ? "₦0.00"
            : new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(a);

    const getPaymentStatus = (payment) => {
        if (!payment) return { label: "Unknown", color: "bg-gray-100 text-gray-700" };
        return payment["Is Paid In Full"]
            ? { label: "Paid in Full", color: "bg-emerald-100 text-emerald-800 border border-emerald-200" }
            : { label: "Partial Payment", color: "bg-amber-100 text-amber-800 border border-amber-200" };
    };

    const openPaymentModal = (payment) => {
        setSelectedPayment(payment);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedPayment(null);
    };

    // Table Column Config
    const columns = [
        { key: "Payment Date", label: "Payment Date", render: (v) => formatDateTime(v) },
        { key: "Invoice Number", label: "Invoice #", render: (_, r) => r["Invoice Number"] || `INV-${r["Order ID"]}` },
        { key: "Customer Name", label: "Customer", render: (_, r) => r["Customer Name"] || "Unknown Customer" },
        { key: "Payment Amount", label: "Payment Amount", render: (v) => <span className="font-semibold text-zinc-900">{formatCurrency(v)}</span> },
        { key: "Credit Sale Amount", label: "Invoice Total", render: (_, r) => formatCurrency(r["Credit Sale Amount"]) },
        {
            key: "Current Balance Owed",
            label: "Balance Remaining",
            render: (_, r) => {
                const remaining = r["Current Balance Owed"] - r["Payment Amount"];
                const display = Math.max(0, remaining);
                return (
                    <span className={`font-semibold ${display > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                        {formatCurrency(display)}
                    </span>
                );
            },
        },
        {
            key: "Is Paid In Full",
            label: "Status",
            render: (_, r) => {
                const { label, color } = getPaymentStatus(r);
                return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>{label}</span>;
            },
        },
        {
            key: "Payment Method",
            label: "Method",
            render: (v) => {
                const methodMap = {
                    bank_transfer: "Bank Transfer",
                    cash: "Cash",
                    card: "Card",
                    pos: "POS",
                    cheque: "Cheque",
                };
                return methodMap[v] || v || "—";
            },
        },
        { key: "Due Date", label: "Due Date", render: (_, r) => formatDate(r["Due Date"]) },
    ];

    const rowActions = [{ key: "view", label: "View Receipt" }];

    const handleRowAction = (actionKey, row) => {
        if (actionKey === "view") openPaymentModal(row);
    };

    const quickActions = [
        { label: "Record Payment", icon: Plus, onClick: () => navigate("/sales/invoices/receive-payment") },
        { label: "Export Payments", icon: FileDown, onClick: () => console.log("Export Payments") },
        { label: "Import Payments", icon: FileUp, onClick: () => console.log("Import Payments") },
    ];

    if (!user) return null;

    return (
        <>
            <div className="min-h-screen bg-zinc-50/50 p-4 sm:p-6 lg:p-8">
                <div className="w-full max-w-7xl mx-auto space-y-6">
                    {/* Header Banner */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-zinc-200/80 shadow-sm">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-xl text-indigo-600">
                                <Receipt size={24} />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-zinc-900 tracking-tight">
                                    Payments Received
                                </h1>
                                <p className="text-xs text-zinc-500 mt-0.5">
                                    Monitor incoming customer transactions and store collections.
                                </p>
                            </div>
                        </div>

                        {/* Controls Bar: Branch Selector & Date Filter */}
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="w-full sm:w-60">
                                <SearchableSelectBox
                                    options={stores}
                                    value={selectedStore}
                                    onChange={(e) => setSelectedStore(e.target.value)}
                                    placeholder="Select branch / store..."
                                    icon={<Store size={16} className="text-zinc-400" />}
                                />
                            </div>

                            <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg p-1.5 mb-4">
                                <button
                                    type="button"
                                    onClick={() => setUseDateFilter(!useDateFilter)}
                                    className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${useDateFilter
                                            ? "bg-white text-indigo-600 shadow-xs border border-zinc-200"
                                            : "text-zinc-500 hover:text-zinc-800"
                                        }`}
                                >
                                    Today Filter
                                </button>
                                {useDateFilter && (
                                    <input
                                        type="date"
                                        value={filterDate}
                                        onChange={(e) => setFilterDate(e.target.value)}
                                        className="text-xs font-medium border-0 bg-transparent text-zinc-700 focus:outline-none focus:ring-0"
                                    />
                                )}
                            </div>

                            <button
                                onClick={loadPayments}
                                title="Refresh Table"
                                className="p-2.5 border border-zinc-200 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors mb-4"
                            >
                                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                            </button>
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-28 text-zinc-400">
                                <Loader2 className="animate-spin h-8 w-8 text-indigo-600 mb-2" />
                                <span className="text-xs font-medium">Loading payments for location...</span>
                            </div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={payments}
                                title=""
                                rowActions={rowActions}
                                onAction={handleRowAction}
                                classNames="border-none"
                            />
                        )}
                    </div>
                </div>

                {/* Floating Quick Actions Menu */}
                <div className="fixed bottom-8 right-8 z-50 flex flex-col items-end">
                    {showActions && (
                        <div className="mb-3 flex flex-col gap-3 items-end animate-in fade-in slide-in-from-bottom-4 duration-200">
                            {quickActions.map((action, idx) => (
                                <div key={idx} className="flex items-center gap-3 group">
                                    <span className="bg-zinc-900 text-white text-xs px-3 py-1.5 rounded shadow-lg font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                        {action.label}
                                    </span>
                                    <button
                                        onClick={() => {
                                            action.onClick();
                                            setShowActions(false);
                                        }}
                                        className="p-3.5 bg-white text-indigo-600 rounded-full shadow-xl hover:bg-indigo-50 transition-all border border-zinc-200 active:scale-95"
                                        title={action.label}
                                    >
                                        <action.icon size={18} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <button
                        onClick={() => setShowActions(!showActions)}
                        className={`flex items-center justify-center w-14 h-14 rounded-full shadow-2xl transition-all duration-300 transform active:scale-90 ${showActions ? "bg-zinc-900 rotate-45" : "bg-indigo-600 hover:bg-indigo-700"
                            } text-white`}
                    >
                        {showActions ? <X size={28} /> : <Plus size={28} />}
                    </button>

                    {showActions && (
                        <div
                            className="fixed inset-0 -z-10 bg-black/10 backdrop-blur-[2px] transition-all"
                            onClick={() => setShowActions(false)}
                        />
                    )}
                </div>
            </div>

            {/* Receipt Details Modal */}
            <Modal isOpen={isModalOpen} onClose={closeModal} title="Payment Receipt" size="wide">
                {selectedPayment && (
                    <div className="max-w-xl mx-auto p-4 text-sm font-mono leading-relaxed bg-white">
                        <div className="text-center border-b-2 border-dashed border-zinc-300 pb-4 mb-4">
                            <h2 className="text-xl font-bold text-zinc-900">PAYMENT RECEIPT</h2>
                            <p className="text-xs text-zinc-500 mt-1">Store / Branch Transaction Record</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6 text-xs">
                            <div>
                                <p className="text-zinc-500">Receipt No:</p>
                                <p className="font-semibold text-zinc-800">PMT-{selectedPayment["Payment ID"]}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-zinc-500">Date & Time:</p>
                                <p className="font-semibold text-zinc-800">{formatDateTime(selectedPayment["Payment Date"])}</p>
                            </div>
                        </div>

                        <div className="border-t border-b border-zinc-200 py-4 mb-6 space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-zinc-500">Invoice Total:</span>
                                <span className="font-semibold">{formatCurrency(selectedPayment["Credit Sale Amount"])}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-zinc-500">Amount Paid:</span>
                                <span className="font-bold text-base text-emerald-600">{formatCurrency(selectedPayment["Payment Amount"])}</span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-zinc-100">
                                <span className="text-zinc-700 font-bold">New Balance:</span>
                                <span className="font-bold text-base text-amber-600">
                                    {formatCurrency(selectedPayment["Current Balance Owed"])}
                                </span>
                            </div>
                        </div>

                        <div className="text-center pt-2">
                            <p className="text-zinc-400 text-[11px]">Thank you for your business!</p>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default PaymentsReceived;